/**
 * Pure function that decides whether an inbound SMS notification should fire
 * given the current rooms snapshot and the previous per-room last-message
 * timestamps.
 *
 * Extracted from conversationsV2.js onSnapshot handler for unit testability.
 *
 * Why pure: the caller is a Vuex store with `commit` and `this.state.*`,
 * which is awkward to test. This function takes all needed state as input
 * and returns what to mutate — the store does the wiring.
 *
 * Unit tests: cypress/node-tests/sms-notification-detection.test.js
 */

export function parseAttrs(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch (_) { return {}; }
  }
  if (typeof raw === 'object') return raw;
  return {};
}

/**
 * @param {object} params
 * @param {Array} params.rooms               Rooms from the current snapshot (post-processing)
 * @param {object} params.prevTsMap          { [roomId]: lastSeenTimestampMs }
 * @param {boolean} params.isFirstSnapshot   True on the very first snapshot after listener setup
 * @param {string|null} params.currentUserID Current employee's userID (maps to lastMessage.author)
 * @param {string|null} params.currentUsername Current employee's username (also checked against author)
 *
 * @returns {{
 *   nextPrevTsMap: object,
 *   notification: null | {
 *     roomId: string,
 *     roomName: string,
 *     content: string
 *   }
 * }}
 */
export function detectInboundSMSNotification({ rooms, prevTsMap, isFirstSnapshot, currentUserID, currentUsername }) {
  const nextPrevTsMap = { ...(prevTsMap || {}) };
  let newest = null;
  let newestTs = 0;

  if (!Array.isArray(rooms)) {
    return { nextPrevTsMap, notification: null };
  }

  rooms.forEach((r) => {
    if (!r || !r.roomId) return;
    const ts = (r.lastMessage && typeof r.lastMessage.timestampNum === 'number') ? r.lastMessage.timestampNum : 0;
    if (!ts) return;

    const prev = nextPrevTsMap[r.roomId] || 0;
    if (ts <= prev) return;

    // Always remember the advance so later snapshots have correct baseline.
    nextPrevTsMap[r.roomId] = ts;

    if (isFirstSnapshot) return; // never notify on initial load

    const attrs = r.lastMessage ? parseAttrs(r.lastMessage.attributes) : {};
    const isReminder = !!(attrs.reminderType || attrs.autoReply);
    const author = r.lastMessage && r.lastMessage.author;
    const isOutbound = !!(
      (currentUserID && author === currentUserID) ||
      (currentUsername && author === currentUsername) ||
      attrs.outbound === true ||
      attrs.sentViaAPI ||
      isReminder
    );

    if (!isOutbound && ts > newestTs) {
      newestTs = ts;
      newest = r;
    }
  });

  if (!newest) {
    return { nextPrevTsMap, notification: null };
  }

  return {
    nextPrevTsMap,
    notification: {
      roomId: newest.roomId,
      roomName: newest.roomName || 'Patient',
      // Prefer lastMessage.content over lastMessagePreview. Reason: Firestore
      // can advance lastMessage.timestampNum before lastMessagePreview
      // propagates, and the room-ingest preservation logic in
      // conversationsV2.js (~line 612) keeps the OLD lastMessagePreview when
      // the new snapshot's preview is empty. That made the push notification
      // body lag by one message — fired with stale preview while
      // lastMessage.content already had the fresh content.
      content: (newest.lastMessage && newest.lastMessage.content)
        || newest.lastMessagePreview
        || 'New SMS message',
    },
  };
}
